import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { ActorRefFrom } from "xstate";
import type { ApiClient } from "@cartero/client";
import type { connectivityMachine } from "@fleet/state-machines";
import { createConnectivityHandlers } from "../auth/controllers";
import { parseMaintenance, setMaintenance } from "@fleet/maintenance";
import { setSyncBeacons } from "../telemetry/client-info";
import {
  type ApiContractVerdict,
  evaluateApiContract,
  readApiContractWindow,
} from "@fleet/version";

export interface ConnectivityManagerOptions {
  /** Function to get the current database instance*/
  getDb: () => AbstractPowerSyncDatabase | null;
  /** Pre-configured OpenAPI client*/
  apiClient: ApiClient;
  /** Reference to the global connectivity state machine actor*/
  connectivityRef: ActorRefFrom<typeof connectivityMachine>;
  /** Callback to invalidate app-wide queries when sync settles*/
  onInvalidateQueries?: () => void;
  /** Frequency of health checks in milliseconds. Defaults to 15s.*/
  healthCheckInterval?: number;
  /**
   * This build's API contract version (see `../version/api-contract`). Omit it
   * and every compatibility check reads `unknown`, i.e. the manager behaves
   * exactly as it did before this feature existed.
   */
  clientContractVersion?: number | null;
  /**
   * Called on every SUCCESSFUL health probe with the compatibility verdict.
   * Never called on a failed probe: a health check that did not answer says
   * nothing about compatibility, and treating "offline" as "incompatible" is
   * how an offline-first app ends up refusing to open its own local data.
   */
  onContractVerdict?: (verdict: ApiContractVerdict) => void;
  /**
   * Called ONCE when the client has been ahead of the server continuously for
   * longer than {@link ConnectivityManagerOptions.aheadGraceMs}. Being ahead is
   * the normal state during a deploy (Pages publishes before the k8s rollout
   * lands), so it is only worth a signal if it OUTLASTS a rollout.
   */
  onClientAheadPersists?: (info: {
    clientVersion: number;
    serverCurrent: number;
    sinceMs: number;
  }) => void;
  /** Grace window before a persistently-ahead client is reported. Defaults to 10 min.*/
  aheadGraceMs?: number;
}

const DEFAULT_HEALTH_CHECK_INTERVAL_MS = 15_000;
const DEFAULT_AHEAD_GRACE_MS = 10 * 60 * 1000;

/**
 * Reads an OPTIONAL text field from an API response.
 *
 * Returns `undefined` if it is missing or is not a non-empty string, so the
 * consumer falls back to its default copy instead of showing the user an
 * unexpected value.
 */
function readOptionalText(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/**
 * Orchestrates the connectivity state between the network, the API, and PowerSync.
 * This is framework-agnostic and handles:
 * 1. Health polling of the backend API.
 * 2. Monitoring PowerSync status and sync progress.
 * 3. Driving the connectivity state machine.
 */
export function startConnectivityManager(options: ConnectivityManagerOptions) {
  const {
    apiClient,
    connectivityRef,
    onInvalidateQueries,
    healthCheckInterval = DEFAULT_HEALTH_CHECK_INTERVAL_MS,
    clientContractVersion = null,
    onContractVerdict,
    onClientAheadPersists,
    aheadGraceMs = DEFAULT_AHEAD_GRACE_MS,
  } = options;

  // --- API contract compatibility ---
  // Kept in memory ON PURPOSE, unlike the self-update budget: being ahead of the
  // server is transitory, so a reload legitimately restarts the clock. Only the
  // outdated path needs a memory that survives reloads.
  let aheadSince: number | null = null;
  let aheadReported = false;

  function evaluateContract(payload: unknown): void {
    const verdict = evaluateApiContract(clientContractVersion, readApiContractWindow(payload));

    if (verdict.kind === "client-ahead") {
      aheadSince ??= Date.now();
      const sinceMs = Date.now() - aheadSince;
      if (!aheadReported && sinceMs > aheadGraceMs) {
        aheadReported = true;
        onClientAheadPersists?.({
          clientVersion: verdict.clientVersion,
          serverCurrent: verdict.serverCurrent,
          sinceMs,
        });
      }
    } else {
      aheadSince = null;
      aheadReported = false;
    }

    onContractVerdict?.(verdict);
  }

  const handlers = createConnectivityHandlers((ev) => {
    connectivityRef.send(ev);
  });

  // --- Health Polling ---
  let healthInterval: ReturnType<typeof setInterval> | null = null;
  // Dedupe overlapping probes: a forced foreground re-check (see
  // `forceHealthCheck`) must be safe to call concurrently with the periodic
  // interval — and with itself — without firing two simultaneous pings or
  // racing two `onApiHealthChanged` writes. While a check is in flight, extra
  // callers await the SAME promise.
  let inFlightCheck: Promise<void> | null = null;

  async function runHealthCheck(): Promise<void> {
    if (import.meta !== undefined && import.meta.env?.SSR) {
      return;
    }
    try {
      const { data, error } = await apiClient.GET("/api/system/health");
      // A gateway error (e.g. 502 with a non-JSON body) can resolve with both
      // `error` and `data` undefined — guard `data` so we don't throw reading
      // `data.status` and instead correctly report the API as down.
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the types say `data` always exists, but a 502 without JSON leaves it undefined at runtime
      if (error || !data) {
        handlers.onApiHealthChanged("down");
        evaluateContract(null);
        // A failed probe says nothing about the maintenance window: leave the
        // last known snapshot in place instead of "lifting" it on a blip.
      } else {
        const payload: Record<string, unknown> = { ...data };
        // The top-level `message` is the LEGACY carrier (a server that predates
        // `maintenance` only has this one); `maintenance.message` wins when
        // present. See the field's doc comment in `system.rs` for why both exist.
        const legacyMessage = readOptionalText(payload, "message");
        const snapshot = parseMaintenance(payload.maintenance, legacyMessage);
        // Published BEFORE the machine event: the upload gate and the
        // auto-connect controller read this synchronously, and a window must
        // never be announced to the UI before the client has stopped writing.
        setMaintenance(snapshot);
        handlers.onApiHealthChanged(
          data.status === "maintenance" ? "maintenance" : "up",
          data.version,
          // Third argument = the MESSAGE shown to the user in the
          // maintenance dialog, NOT server metadata. Previously this passed
          // `data.engine`, so the first maintenance notice would have
          // told the user "postgres".
          snapshot.message ?? legacyMessage,
          snapshot.scopes,
        );
        // Only on a SUCCESSFUL probe. A health check that failed tells us
        // nothing about version compatibility, and an offline-first app must
        // never turn "no network" into "you must update".
        evaluateContract(data);
      }
    } catch (err) {
      console.warn("[ConnectivityManager] Health check failed:", err);
      handlers.onApiHealthChanged("down");
      evaluateContract(null);
    }
  }

  function checkHealth(): Promise<void> {
    inFlightCheck ??= runHealthCheck().finally(() => {
      inFlightCheck = null;
    });
    return inFlightCheck;
  }

  function stopHealthCheck() {
    if (healthInterval) {
      clearInterval(healthInterval);
      healthInterval = null;
    }
  }

  function startHealthCheck(): Promise<void> {
    stopHealthCheck();
    const check = checkHealth();
    healthInterval = setInterval(() => {
      void checkHealth();
    }, healthCheckInterval);
    return check;
  }

  /**
   * Immediately re-probe the API, out of band from the periodic interval.
   *
   * Why this exists: on mobile, sending the tab to the background suspends /
   * throttles the `setInterval` health poll (and any in-flight probe may fail),
   * so `apiStatus` can latch to "down". Returning to the foreground fires no
   * `online` event (the network never actually changed), so nothing forced a
   * re-check and the connectivity machine stayed "down" until a throttled timer
   * eventually ran or the user refreshed. The foreground listener in the web
   * provider calls this to re-probe instantly.
   *
   * When the periodic poll is active we RESTART it around this probe so the
   * cadence realigns after a long background gap (otherwise the next scheduled
   * tick could be far in the future). When polling is stopped (e.g. offline) we
   * just run a one-off, dedup'd check. Safe to call concurrently with the
   * interval — `checkHealth` guards against overlapping in-flight probes.
   */
  function forceHealthCheck(): Promise<void> {
    return healthInterval ? startHealthCheck() : checkHealth();
  }

  // --- PowerSync Status Listener ---
  let unsubscribe: (() => void) | null = null;
  let prevDownloading = false;
  let prevHasSynced = false;

  function setupDbListener(db: AbstractPowerSyncDatabase) {
    if (unsubscribe) {
      unsubscribe();
    }

    handlers.onPowerSyncStatusChanged({
      isConnected: db.connected,
      isConnecting: db.currentStatus.connecting,
      dbError: null,
    });

    unsubscribe = db.registerListener({
      statusChanged: (s) => {
        handlers.onPowerSyncStatusChanged({
          isConnected: s.connected,
          isConnecting: s.connecting,
          dbError: null,
        });

        const { downloading } = s.dataFlowStatus;
        const hasSynced = s.hasSynced ?? false;

        // Tier-1 telemetry beacons. Piggy-backs on the status listener that already runs
        // on every change rather than polling: these are read from module state only when
        // a request is about to go out, so writing them here costs two assignments and no
        // I/O.
        //
        // `lastSyncedAt` is the age of the last SUCCESSFUL sync, which is the honest
        // liveness measure — "connected" can be true while nothing has actually landed.
        // `uploadQueueStats` is read defensively because it is an optional part of the
        // status shape and a missing field must degrade to "unknown", never to a
        // fabricated 0 that would read as "all clear".
        //
        // Read through a widened record instead of a narrowing cast: the shape of
        // `dataFlowStatus` belongs to the PowerSync SDK and changes between minor
        // versions, so asserting a narrower type here is both unsound and a lint error
        // (`typescript/no-unsafe-type-assertion`).
        const lastSyncedAt = s.lastSyncedAt ?? null;
        const flow: Record<string, unknown> = { ...s.dataFlowStatus };
        const uploadQueueStats: Record<string, unknown> =
          typeof flow.uploadQueueStats === "object" && flow.uploadQueueStats !== null
            ? { ...flow.uploadQueueStats }
            : {};
        const queued: unknown = uploadQueueStats.count;
        setSyncBeacons({
          lastSyncedAtMs: lastSyncedAt === null ? null : lastSyncedAt.getTime(),
          queueDepth: typeof queued === "number" ? queued : null,
        });

        // Force cache invalidation when sync settles (first time or after batch)
        if ((!prevHasSynced && hasSynced) || (prevDownloading && !downloading)) {
          onInvalidateQueries?.();
        }

        prevDownloading = downloading ?? false;
        prevHasSynced = hasSynced;
      },
    });
  }

  return {
    handlers,
    startHealthCheck,
    stopHealthCheck,
    forceHealthCheck,
    setupDbListener,
    dispose: () => {
      stopHealthCheck();
      unsubscribe?.();
    },
  };
}
